import type { FileUploadItem } from "@Reducers/minimiseFileUpload";

export interface ReconciliationRow {
  fileName: string;
  location: string;
  fileType: string;
}

export interface ReconciliationCounts {
  emptyContainersCount: number;
  unmatchedFilesCount: number;
  hasGaps: boolean;
}

export interface ReconciliationRows {
  emptyContainers: ReconciliationRow[];
  unmatchedFiles: ReconciliationRow[];
}

export interface DirectImportContainer {
  name?: string;
  fileFormat?: string;
  folderPath?: string;
}

type ContainerLike = DirectImportContainer | Record<string, unknown> | null | undefined;

export const containerKey = (container: ContainerLike): string =>
  `${String(container?.name ?? "")}|${String(container?.fileFormat ?? "")}|${String(container?.folderPath ?? "")}`;

const getExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1) : "";
};

const toArray = <T>(value: T[]): T[] => (Array.isArray(value) ? value : []);

const buildMatchedContainerKeys = (files: FileUploadItem[]): Set<string> =>
  new Set(
    files
      .filter((file) => file && !file.isDeleted && !file.error && file.matchedDirectImportContainer)
      .map((file) => containerKey(file.matchedDirectImportContainer)),
  );

const isEmptyContainer = (container: DirectImportContainer, matchedKeys: Set<string>): boolean =>
  Boolean(container) && !matchedKeys.has(containerKey(container));

const isUnmatchedFile = (file: FileUploadItem): boolean =>
  Boolean(file) && !file.isDeleted && !file.isFolder && !file.matchedDirectImportContainer;

export const getReconciliationCounts = (
  fileUploadList: FileUploadItem[] = [],
  directImportContainers: DirectImportContainer[] = [],
): ReconciliationCounts => {
  const files = toArray(fileUploadList);
  const matchedKeys = buildMatchedContainerKeys(files);
  const emptyContainersCount = toArray(directImportContainers).filter((container) =>
    isEmptyContainer(container, matchedKeys),
  ).length;
  const unmatchedFilesCount = files.filter(isUnmatchedFile).length;
  return {
    emptyContainersCount,
    unmatchedFilesCount,
    hasGaps: emptyContainersCount > 0 || unmatchedFilesCount > 0,
  };
};

export const getReconciliationRows = (
  fileUploadList: FileUploadItem[] = [],
  directImportContainers: DirectImportContainer[] = [],
): ReconciliationRows => {
  const files = toArray(fileUploadList);
  const matchedKeys = buildMatchedContainerKeys(files);
  const emptyContainers: ReconciliationRow[] = toArray(directImportContainers)
    .filter((container) => isEmptyContainer(container, matchedKeys))
    .map((container) => ({
      fileName: container.name ?? "",
      location: container.folderPath ?? "",
      fileType: String(container.fileFormat ?? "").toUpperCase(),
    }));
  const unmatchedFiles: ReconciliationRow[] = files.filter(isUnmatchedFile).map((file) => ({
    fileName: file.name ?? "",
    location: file.uploadLocation ?? "",
    fileType: getExtension(file.name ?? "").toUpperCase(),
  }));
  return { emptyContainers, unmatchedFiles };
};
